import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { trackGoal } from '@shared/analytics';
import { clearAttribution } from '@shared/lib/attribution';
import {
  signIn as signInApi,
  signOut as signOutApi,
  signUp as signUpApi,
  type AuthResult,
} from '../api/userApi';
import { rememberPendingLanguage, uiLang } from '../lib/interfaceLanguage';
import { useAuthStore } from './useAuthStore';

interface SignInPayload {
  email: string;
  password: string;
}

interface SignUpPayload {
  /** После демо игры имя не спрашиваем (2026-10-30): без поля сервер пишет пустое имя. */
  name?: string;
  email: string;
  password: string;
}

export function useSignInMutation() {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const setStage = useAuthStore((s) => s.setStage);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  return useMutation<AuthResult, Error, SignInPayload>({
    mutationFn: signInApi,
    onSuccess: ({ user, accessToken }) => {
      qc.clear();
      setAccessToken(accessToken);
      setUser(user);
      setStage('app');
      trackGoal('login');
      // Аккаунт уже есть: запомненные метки не должны достаться чужой регистрации в этом браузере.
      clearAttribution();
    },
  });
}

export function useSignUpMutation() {
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const setUser = useAuthStore((s) => s.setUser);
  const setStage = useAuthStore((s) => s.setStage);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  // Язык, на котором ученик регистрируется (B4): уходит в теле и ждёт записи в профиль.
  const currentLang = () => uiLang(i18n.resolvedLanguage || i18n.language);

  return useMutation<AuthResult, Error, SignUpPayload>({
    mutationFn: (payload) => signUpApi({ ...payload, language: currentLang() }),
    onSuccess: ({ user, accessToken }) => {
      qc.clear();
      setAccessToken(accessToken);
      setUser(user);
      rememberPendingLanguage(currentLang(), user.id);
      setStage('onboarding');
      trackGoal('registration'); // ключевая конверсия: привязка канала (UTM) к регистрации
      clearAttribution(); // источник ушёл в теле регистрации (B)
    },
  });
}

export function useSignOutMutation() {
  const qc = useQueryClient();
  const signOut = useAuthStore((s) => s.signOut);

  return useMutation<void, Error, void>({
    mutationFn: signOutApi,
    onSettled: () => {
      signOut();
      qc.clear();
    },
  });
}
